<script lang="ts" setup>
import { AppButton, AppPassword } from '@/core/components';
import type { FormErrors } from '@/types/Form';
import { parsedError } from '@/utils/Helpers';
import { useVuelidate } from '@vuelidate/core';
import { computed, ref } from 'vue';
import type { ForgotPasswordResetPasswordForm } from '../../types';
import { AppPasswordCriteria } from '@/core/components/fields';

const props = withDefaults(
  defineProps<{
    username: string;
    token: string;
    nextPage?: string;
  }>(),
  {
    nextPage: '',
  }
);

const { validations } = useValidation();

const showPassword = ref(false);
const showPasswordConfirmation = ref(false);

const form = ref({
  password: '',
  password_confirmation: '',
});

const rules = () => {
  return {
    password: {
      required: validations.required('Password'),
      minLength: validations.minLength('Password', 8),
      password: validations.password(),
    },
    password_confirmation: {
      required: validations.required('Password Confirmation'),
      sameAs: validations.sameAs('Password Confirmation', 'Password', form.value.password),
    },
  };
};

const router = useRouter();

const v$ = useVuelidate(rules, form, { $autoDirty: true });

const {
  pending: loadingResetPassword,
  execute: execResetPassword,
  error: serverError,
} = useSubmit<{}, { data: FormErrors<ForgotPasswordResetPasswordForm> }>();

const handleSignUp = async (): Promise<void> => {
  const result = await v$.value.$validate();
  if (!result) return;

  await execResetPassword('/auth/reset-password?X-NO-AUTH=true', {
    method: 'POST',
    body: { ...form.value, username: props.username, token: props.token },
  });

  if (!serverError.value) {
    router.push({ name: props.nextPage !== '' ? props.nextPage : 'login' });
  }
};

const errors = computed(() => {
  return parsedError(form.value, v$, serverError.value?.data?.errors || {});
});
</script>

<template>
  <v-form @submit.prevent="handleSignUp">
    <AppPassword
      v-model="form.password"
      label="Password"
      :disabled="loadingResetPassword"
      :error-messages="errors.password"
      data-testid="password"
      data-cy="input-password"
      autofocus
      class="mb-1"
    >
    </AppPassword>

    <AppPassword
      v-model="form.password_confirmation"
      label="Confirm Password"
      :disabled="loadingResetPassword"
      :error-messages="errors.password_confirmation"
      data-testid="password-confirmation"
      data-cy="input-password-confirmation"
    >
    </AppPassword>

    <AppPasswordCriteria :password="form.password" />

    <AppButton data-testid="continue" block class="mt-4 font-weight-bold" :loading="loadingResetPassword">
      Confirm
    </AppButton>
  </v-form>
</template>

<style lang="scss" scoped>
.v-card {
  margin-top: 132px;
}
</style>
